import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { PlatformSyncUpgrade } from 'shared/base';
import { paths } from 'shared/base/constants/paths';
import { generateUrl } from 'shared/base/utils/url';

interface Props {
  newVersion: string;
  newVersionReleaseDate: Date;
  oldVersion: string;
  pluginUpgrades: PlatformSyncUpgrade[];
}

const getChangeLogUrl = (upgrade: PlatformSyncUpgrade) =>
  generateUrl({
    pathname: paths.hub.pluginVersions,
    query: {
      kind: upgrade.kind,
      name: upgrade.path.split('/')[1],
      team: upgrade.path.split('/')[0],
      version: upgrade.new_version,
    },
  });

export const PlatformVersionUpgradeDetailsLayout = ({
  newVersion,
  newVersionReleaseDate,
  oldVersion,
  pluginUpgrades,
}: Props) => (
  <Stack gap={2}>
    <Typography variant="h6">
      Upgraded Platform from {oldVersion} <ArrowForwardIcon sx={{ verticalAlign: 'bottom' }} />{' '}
      {newVersion}
    </Typography>
    <Stack sx={{ color: 'text.secondary' }}>
      <Typography fontWeight={700}>
        Date:{' '}
        <Typography component="span">
          {newVersionReleaseDate.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </Typography>
      </Typography>
      <Typography fontWeight={700}>
        New version: <Typography component="span">{newVersion}</Typography>{' '}
      </Typography>
    </Stack>
    {pluginUpgrades.map((upgrade) => (
      <Stack key={upgrade.path} gap={2} paddingBlock={2}>
        <Stack alignItems="center" direction="row" gap={1}>
          <Typography variant="h6">
            Upgraded {upgrade.path} {upgrade.prev_version}
          </Typography>
          <ArrowForwardIcon />
          <Typography variant="h6">{upgrade.new_version}</Typography>
        </Stack>
        <Stack sx={{ color: 'text.secondary' }}>
          <Stack alignItems="baseline" direction="row" gap={1}>
            <Typography fontWeight={700}>Date:</Typography>
            <Typography>{upgrade.created_at}</Typography>
          </Stack>
          <Stack alignItems="baseline" direction="row" gap={1}>
            <Typography fontWeight={700}>New version:</Typography>
            <Typography>{upgrade.new_version}</Typography>
          </Stack>
        </Stack>
        <Button
          href={getChangeLogUrl(upgrade)}
          LinkComponent={Link}
          sx={{ width: 'fit-content' }}
          target="_blank"
          variant="text"
        >
          <Stack alignItems="center" direction="row" gap={1}>
            <Typography variant="body2">View changelog</Typography>
            <OpenInNewIcon color="primary" />
          </Stack>
        </Button>
        <Typography>
          The following {upgrade.kind === 'source' ? 'integrations' : 'destinations'} have been
          updated as part of this upgrade
        </Typography>
        <Stack gap={1}>
          {upgrade.sync_source_destination_display_names.map((name) => (
            <Stack key={name} alignItems="center" direction="row" gap={1}>
              <CheckIcon color="primary" />
              <Typography>{name}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    ))}
  </Stack>
);
